import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    Link,
    CircularProgress,
    Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

// Styled component for file input
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * Modal component for uploading a file to download processed information.
 */
const FileDownloadModal = ({
    open,
    onClose,
    title = 'Upload File to Download Data',
    description = 'Upload an Excel file to generate and download a report.',
    acceptedFileTypes = '.xls,.xlsx',
    processUploadFn,  // Function to process the upload and return the download
}) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Reset state when modal opens or closes
    React.useEffect(() => {
        if (!open) {
            // Reset state when modal closes
            setFile(null);
            setLoading(false);
            setError(null);
        }
    }, [open]);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError(null);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            setError('Please select a file first.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();
            formData.append('file', file);

            const response = await processUploadFn(formData);

            // Create a download link for the blob response
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Try to get the filename from the Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'download.csv';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Close the modal after successful download
            onClose();

        } catch (err) {
            console.error('Error processing file:', err);

            let errorMessage = 'Failed to process the file. Please try again.';

            if (err.response && err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text(); // Convert Blob to text
                    const json = JSON.parse(text); // Try to parse JSON
                    errorMessage = text || errorMessage;
                } catch (parseErr) {
                    console.warn('Failed to parse error response as JSON', parseErr);
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={!loading ? onClose : undefined}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {title}
                    {!loading && (
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {description}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                <Box
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: 'background.paper'
                    }}
                >
                    {loading ? (
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <CircularProgress size={40} />
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                Processing your file...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                            <Typography variant="body1" gutterBottom>
                                {file ? file.name : 'Drag & drop your file here or click to browse'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {file
                                    ? `${(file.size / 1024).toFixed(2)} KB - ${file.type || 'Unknown type'}`
                                    : `Supported formats: ${acceptedFileTypes}`
                                }
                            </Typography>

                            <Button
                                component="label"
                                variant="contained"
                                sx={{ mt: 2 }}
                                startIcon={<CloudUploadIcon />}
                            >
                                {file ? 'Change File' : 'Select File'}
                                <VisuallyHiddenInput
                                    type="file"
                                    accept={acceptedFileTypes}
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!file || loading}
                    color="primary"
                >
                    {loading ? 'Processing...' : 'Process & Download'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileDownloadModal;
